import { z } from "zod";

/**
 * The product funnel, as TEN server-emitted events — the exact ten Privacy
 * Policy 2.10 §3.2 names and publishes (rulings R-0912-1, R-0916-1, R-0923-6).
 *
 * WHY THE LIST IS CLOSED, AND WHY IT IS NOW ALSO A PUBLISHED PROMISE. §1.7
 * names a funnel — signed in, door opened, first knock, standing reached, trial
 * started, converted, plan changed, paywall seen, limit hit, cancelled — and
 * nothing else is measured here. Privacy 2.10 §3.2 prints that list to the
 * world, so the array below is a served fact: an eleventh member would make
 * the policy untrue. `pack_purchased` and `autoreload_enabled` left with the
 * things they counted (ruling R-0916-1), and `reset_purchased` with the paid
 * reset (R-0923-3 (1)), once the Privacy text that named it was retired
 * (R-0923-6). An
 * open event name is how an analytics estate becomes a landfill where the one
 * number anybody wanted is computed four incompatible ways; a zod enum is the
 * cheapest possible refusal of that.
 *
 * WHY IT LIVES IN `@nob/schemas` AND NOT IN THE API. `apps/web/src/lib/telemetry.ts`
 * keeps its own, separate catalogue — Faro, operations telemetry, no user id
 * (PRD Appendix B). These two lists must never be confused for one another, and
 * a reviewer comparing them should be able to open both without reading an api
 * module. The names deliberately do NOT match the client catalogue's
 * (`plan_cancelled` is in both, `knock_started` is in both) because they are
 * different measurements of the same moment: one is a browser's, one is the
 * server's, and only the server's carries an account id.
 */
export const ANALYTICS_EVENTS = [
  "signed_in",
  "door_created",
  "knock_started",
  "standing_reached",
  "trial_started",
  "trial_converted",
  "plan_changed",
  "paywall_shown",
  "limit_reached",
  "plan_cancelled",
] as const;
export const AnalyticsEvent = z.enum(ANALYTICS_EVENTS);
export type AnalyticsEvent = z.infer<typeof AnalyticsEvent>;

/** What an event property may be. Objects and arrays are not properties. */
export type AnalyticsValue = string | number | boolean | null;

/**
 * THE PROPERTY ALLOWLIST, PER EVENT — the second half of ruling R-0912-1 rule 1
 * ("properties that pass the same redaction as telemetry: never a handle, never
 * knock content, never a rule").
 *
 * Redaction is a mask on a value; this is a refusal of the KEY. A masked handle
 * is still a column called `handle` in somebody's dashboard, still a thing a
 * future call site fills in, and still one schema change away from being
 * unmasked. So the shape of every event is written down once, here, and
 * anything a call site hands over that is not on its event's list is dropped
 * before the adapter sees it (`apps/api/src/analytics/track.ts`).
 *
 * The vocabulary is deliberately tiny and categorical — a tier, an interval, a
 * band, a source, a side, a kind, a surface. Every one of them is a value from
 * a closed set the product already names somewhere else. None of them is text
 * somebody typed, and none of them identifies a door.
 *
 * `signed_in` carries its two on the PERSON (`set`), not on the event: a tier
 * and a signup date are facts about the account, and repeating them on every
 * event is how a property becomes a fact with eleven conflicting versions. The
 * list governs `set` and `properties` alike, because both leave the process.
 */
export const ANALYTICS_EVENT_PROPERTIES: Record<AnalyticsEvent, readonly string[]> = {
  /** On the person: the rung they are on, and the day the account began. */
  signed_in: ["tier", "created_at"],
  /** Which of the two onboarding flows opened it, when the route knows. */
  door_created: ["source"],
  /** Always `"knocker"`. The owner's side of a door emits nothing here. */
  knock_started: ["side"],
  /** The band the knocker was shown, and whether it cleared the door's bar. */
  standing_reached: ["band", "through"],
  trial_started: ["tier", "interval"],
  trial_converted: ["tier", "interval"],
  plan_changed: ["from", "to", "interval"],
  /** Where the funding card was raised — `"knock"` today. */
  paywall_shown: ["surface"],
  /** Which limit — `"knock_rate"` today. */
  limit_reached: ["kind"],
  /** The rung that ended, and whether it was a decision or a dunning failure. */
  plan_cancelled: ["tier", "kind"],
};

/**
 * Drop every key this event did not declare. Returns `undefined` for an empty
 * result so a call site that handed over nothing usable sends no bag at all
 * rather than an empty object the vendor would store as a property set.
 */
export function allowedAnalyticsProperties(
  event: AnalyticsEvent,
  bag: Record<string, AnalyticsValue> | undefined,
): Record<string, AnalyticsValue> | undefined {
  if (!bag) return undefined;
  const allowed = ANALYTICS_EVENT_PROPERTIES[event];
  const out: Record<string, AnalyticsValue> = {};
  for (const [key, value] of Object.entries(bag)) {
    if (allowed.includes(key)) out[key] = value;
  }
  return Object.keys(out).length > 0 ? out : undefined;
}
